import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CompanyInfo = ({ company }) => {
  const navigate = useNavigate();

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name={index < Math.floor(rating) ? "Star" : "Star"}
        size={16}
        className={index < Math.floor(rating) ? "text-accent fill-accent" : "text-muted-foreground"}
      />
    ));
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 lg:p-8">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted border border-border flex-shrink-0">
          <Image
            src={company?.logo}
            alt={company?.logoAlt}
            className="w-full h-full object-contain p-2"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-foreground mb-1">{company?.name}</h2>
          <p className="text-sm text-muted-foreground">{company?.industry}</p>
        </div>
      </div>
      <div className="space-y-4 mb-6">
        <div className="flex items-center gap-3">
          <Icon name="Building2" size={18} className="text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">Company Size</p>
            <p className="text-sm text-muted-foreground">{company?.employeeCount} employees</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Icon name="MapPin" size={18} className="text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">Headquarters</p>
            <p className="text-sm text-muted-foreground">{company?.headquarters}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Icon name="Calendar" size={18} className="text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">Founded</p>
            <p className="text-sm text-muted-foreground">{company?.founded}</p>
          </div>
        </div>

        {company?.website && (
          <div className="flex items-center gap-3">
            <Icon name="Globe" size={18} className="text-muted-foreground flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">Website</p>
              <a
                href={company?.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline truncate block"
              >
                {company?.website}
              </a>
            </div>
          </div>
        )}
      </div>
      <div className="border-t border-border pt-6 mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-3">About Company</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{company?.overview}</p>
      </div>
      {company?.reviews && (
        <div className="border-t border-border pt-6 mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Employee Reviews</h3>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center gap-1">
              {renderStars(company?.reviews?.overallRating)}
            </div>
            <span className="text-2xl font-bold text-foreground">{company?.reviews?.overallRating?.toFixed(1)}</span>
            <span className="text-sm text-muted-foreground">({company?.reviews?.totalReviews} reviews)</span>
          </div>

          <div className="space-y-3">
            {company?.reviews?.categories?.map((category, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-foreground">{category?.name}</span>
                  <span className="text-sm font-medium text-foreground">{category?.rating?.toFixed(1)}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-accent rounded-full h-2 transition-smooth"
                    style={{ width: `${(category?.rating / 5) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <Button
        variant="outline"
        fullWidth
        iconName="ExternalLink"
        iconPosition="right"
        onClick={() => navigate('/company-profile')}
      >
        View Company Profile
      </Button>
    </div>
  );
};

export default CompanyInfo;